import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import TimeClockBar from './TimeClockBar';
import './theme.css';

// Declarative nav with a roles field + filter line (not per-role arrays).
// @@NAV_SECTIONS_v99@@ Order = menu order. `section` groups items under a sidebar label;
// items with no section sit at the top. Add new items inside their section block.
const NAV = [
  {
    to: '/', label: 'Dashboard', roles: ['admin', 'manager', 'staff', 'user'],
    icon: (<svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/></svg>),
  },
  {
    to: '/tasks', label: 'Tasks', roles: ['admin', 'manager', 'staff'], badgeKey: 'tasks',
    icon: (<svg viewBox="0 0 24 24"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>),
  },
  { // @@RECEIPTS_v107@@ receipts & invoices inbox; badge = waiting to be processed (admin/manager)
    to: '/receipts', label: 'Receipts', roles: ['admin', 'manager', 'staff', 'user'], badgeKey: 'receipts',
    icon: (<svg viewBox="0 0 24 24"><path d="M5 2h14v20l-3-2-2 2-2-2-2 2-2-2-3 2z"/><path d="M9 7h6"/><path d="M9 11h6"/><path d="M9 15h3"/></svg>),
  },
  {
    to: '/leads', section: 'Sales', label: 'Leads', roles: ['admin', 'manager'],
    icon: (<svg viewBox="0 0 24 24"><path d="M22 12h-6l-2 3h-4l-2-3H2"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>),
  },
  { // @@QUICKQUOTE_NAV_v75@@
    to: '/quick-quote', section: 'Sales', label: 'Quick Quote', roles: ['admin', 'manager', 'staff', 'user'],
    icon: (<svg viewBox="0 0 24 24"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M8 6h8"/><path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 19h.01"/></svg>),
  },
  { // @@QUOTES_READY_v106@@ open Zoho estimates + Ready to Invoice
    to: '/quotes', section: 'Sales', label: 'Open Quotes', roles: ['admin', 'manager'],
    icon: (<svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 15l2 2 4-4"/></svg>),
  },
  {
    to: '/contacts', section: 'Sales', label: 'Contacts', roles: ['admin', 'manager', 'staff', 'user'],
    icon: (<svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
  },
  {
    to: '/clients', section: 'Clients', label: 'Clients', roles: ['admin', 'manager', 'staff'],
    icon: (<svg viewBox="0 0 24 24"><path d="M3 21h18"/><path d="M5 21V7l7-4 7 4v14"/><path d="M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"/></svg>),
  },
  {
    to: '/shopify-tax', section: 'Clients', label: 'Sales Tax', roles: ['admin', 'manager', 'staff'], // @@SHOPIFY_TAX_REPORT_v71@@
    icon: (<svg viewBox="0 0 24 24"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>),
  },
  { // @@EQUIPMENT_LEASES_v87@@ leased card readers etc.; badge = awaiting client + past end date
    to: '/equipment', section: 'Clients', label: 'Equipment', roles: ['admin', 'manager', 'staff'], badgeKey: 'equipment',
    icon: (<svg viewBox="0 0 24 24"><rect x="6" y="2" width="12" height="20" rx="2"/><path d="M9 6h6"/><rect x="9" y="9" width="6" height="4" rx="1"/><path d="M9 17h.01M12 17h.01M15 17h.01"/></svg>),
  },
  { // @@COLLECTIONS_v123@@ past-due clients; badge = clients with an invoice more than 30 days past due
    to: '/collections', section: 'Clients', label: 'Collections', roles: ['admin', 'manager'], badgeKey: 'collections',
    icon: (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/><path d="M16.5 3.5 19 6"/></svg>),
  },
  {
    to: '/purchase-orders', section: 'Production', label: 'Purchase Orders', roles: ['admin', 'manager', 'staff', 'user'], badgeKey: 'pos',
    icon: (<svg viewBox="0 0 24 24"><path d="M9 2h6l1 4H8z"/><path d="M5 6h14v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"/><path d="M9 12h6"/><path d="M9 16h6"/></svg>),
  },
  {
    to: '/production', section: 'Production', label: 'Production', roles: ['admin', 'manager', 'staff', 'user'], badgeKey: 'production',
    icon: (<svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M3 10h18"/><path d="M8 2v4"/><path d="M16 2v4"/></svg>),
  },
  { // @@TECH_PACKS_v94@@ badge = packs a client sent back with changes
    to: '/tech-packs', section: 'Production', label: 'Tech Packs', roles: ['admin', 'manager', 'staff', 'user'], badgeKey: 'techpacks',
    icon: (<svg viewBox="0 0 24 24"><path d="M8 3 4 6v4l3-1v12h10V9l3 1V6l-4-3c-.5 1.5-2 2.5-4 2.5S8.5 4.5 8 3z"/><path d="M10 12h4"/></svg>),
  },
  {
    to: '/vendors', section: 'Production', label: 'Vendors', roles: ['admin', 'manager', 'staff', 'user'], badgeKey: 'vendors',
    icon: (<svg viewBox="0 0 24 24"><path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="M3 9l2-5h14l2 5"/><path d="M9 13h6"/></svg>),
  },
  { // @@SHIP_SECTION_v81@@
    to: '/ship', section: 'Shipping', label: 'Ship', roles: ['admin', 'manager'],
    icon: (<svg viewBox="0 0 24 24"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>),
  },
  {
    to: '/packages', section: 'Shipping', label: 'Packages', roles: ['admin', 'manager', 'staff', 'user'],
    icon: (<svg viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.27 6.96 8.73 5.04 8.73-5.04"/><path d="M12 22.08V12"/></svg>),
  },
  { // @@CARRIER_BILLS_v119@@ FedEx invoices -> Zoho bills; badge = imported, not yet in Zoho
    to: '/carrier-bills', section: 'Shipping', label: 'Carrier Bills', roles: ['admin', 'manager'], badgeKey: 'carrierbills',
    icon: (<svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M12 18v-6"/><path d="M9.5 13.5c0-.8.9-1.5 2.5-1.5s2.5.6 2.5 1.4c0 1.9-5 1-5 3 0 .8 1 1.6 2.5 1.6s2.5-.7 2.5-1.5"/></svg>),
  },
  {
    to: '/timeclock', section: 'Team', label: 'Time Clock', roles: ['admin', 'manager'],
    icon: (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>),
  },
  { // @@JOBS_v86@@
    to: '/hiring', section: 'Team', label: 'Hiring', roles: ['admin', 'manager'], badgeKey: 'jobs',
    icon: (<svg viewBox="0 0 24 24"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>),
  },
  {
    to: '/users', section: 'Team', label: 'Users', roles: ['admin'],
    icon: (<svg viewBox="0 0 24 24"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
  },
  {
    to: '/signature', section: 'Team', label: 'Signature', roles: ['admin', 'manager', 'staff', 'user'],
    icon: (<svg viewBox="0 0 24 24"><path d="M3 17c3-1 5-9 8-9s2 6 5 6 3-2 5-2"/><path d="M3 21h18"/></svg>),
  },
  { // @@SITE_v85@@
    to: '/website', section: 'Admin', label: 'Website', roles: ['admin', 'manager'], badgeKey: 'site',
    icon: (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18"/></svg>),
  },
  {
    to: '/settings', section: 'Admin', label: 'Settings', roles: ['admin', 'manager'],
    icon: (<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>),
  },
];

function initials(user) {
  if (!user) return '';
  const f = (user.first_name || '').trim();
  const l = (user.last_name || '').trim();
  const i = (f[0] || '') + (l[0] || '');
  return (i || (user.email || '?')[0]).toUpperCase();
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = NAV.filter((i) => !i.roles || (user && i.roles.includes(user.role)));
  const [counts, setCounts] = useState({});
  useEffect(() => {
    let active = true;
    apiFetch('/api/vendors/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, vendors: d.count })); })
      .catch(() => {});
    apiFetch('/api/pos/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, pos: d.count })); })
      .catch(() => {});
    apiFetch('/api/production/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, production: d.count })); })
      .catch(() => {});
    apiFetch('/api/tasks/count?assigned=me')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, tasks: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // @@RECEIPTS_v107@@ receipts waiting to be processed — admin/manager only, best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/receipts/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, receipts: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // @@COLLECTIONS_v123@@ clients with an invoice more than 30 days past due — admin/manager only, best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/collections/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, collections: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // @@CARRIER_BILLS_v119@@ carrier invoices imported but not yet sent to Zoho — admin/manager only, best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/carrier-bills/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, carrierbills: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // @@TECH_PACKS_v94@@ tech packs with client-requested changes. Best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager', 'staff', 'user'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/techpacks/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, techpacks: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // @@EQUIPMENT_LEASES_v87@@ leases awaiting client acceptance + active leases past end date. Best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager', 'staff'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/equipment/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, equipment: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // @@JOBS_v86@@ unread job applications — admin/manager only, best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/jobs/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, jobs: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // @@SITE_v85@@ unseen website inquiries — admin/manager only, best-effort.
  useEffect(() => {
    if (!user || !['admin', 'manager'].includes(user.role)) return undefined;
    let active = true;
    apiFetch('/api/site/count')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d) setCounts((c) => ({ ...c, site: d.count })); })
      .catch(() => {});
    return () => { active = false; };
  }, [user, location.pathname]);

  // Active (non-delivered) package count for the nav badge. Best-effort:
  // on any failure we just show no badge rather than blocking the nav.
  const [pkgCount, setPkgCount] = useState(null);
  useEffect(() => {
    let active = true;
    apiFetch('/api/packages/count')
      .then((res) => (res.ok ? res.json() : null))
      .then((d) => { if (active && d) setPkgCount(d.active); })
      .catch(() => {});
    return () => { active = false; };
  }, [location.pathname]);

  function handleLogout() { logout(); navigate('/login'); }

  const fullName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : '';

  return (
    <div className="hq-app">
      <aside className="hq-side">
        <div className="hq-brand">
          <img src="/brand/xyz-mark.png" alt="XYZ Merchandise" />
          <div className="hq-wm">XYZ<span>Merchandise</span></div>
        </div>
        <nav className="hq-nav">
          {items.map((i, idx) => (
            <React.Fragment key={i.to}>
            {i.section && (idx === 0 || items[idx - 1].section !== i.section) && (
              <div className="hq-nav-section">{i.section}</div>
            )}
            <Link
              to={i.to}
              className={(location.pathname === i.to || (i.to !== '/' && location.pathname.startsWith(i.to + '/'))) ? 'hq-active' : ''}
            >
              {i.icon}{i.label}
              {i.badgeKey && counts[i.badgeKey] > 0 && (
                <span className="hq-nav-badge">{counts[i.badgeKey]}</span>
              )}
              {i.to === '/packages' && pkgCount > 0 && (
                <span className="hq-nav-badge">{pkgCount}</span>
              )}
            </Link>
            </React.Fragment>
          ))}
        </nav>
        <div className="hq-side-foot">
          <div className="hq-user">
            <div className="hq-avatar">{initials(user)}</div>
            <div className="hq-who">
              <div className="hq-name">{fullName}</div>
              <small>{user && user.role}</small>
            </div>
          </div>
          <button className="hq-signout" onClick={handleLogout}>Sign out</button>
        </div>
      </aside>
      <main className="hq-content"><TimeClockBar />{children}</main>
    </div>
  );
}
